import { matchPath } from 'react-router-dom';
import { ROUTE_PATHS } from './routePaths';

/**
 * All path patterns the SPA registers (see routes/index.tsx). Used to detect URLs that are not
 * part of this app (e.g. legacy /ENDUSER/previewPage.cfm) and send them to 404.
 */
function collectKnownRoutePatterns(): Array<string> {
  const patterns = new Set<string>();
  for (const [key, value] of Object.entries(ROUTE_PATHS)) {
    if (key === 'NOT_FOUND') continue;
    if (typeof value === 'string' && value !== '*' && value.length > 0) {
      patterns.add(value);
    }
  }
  // Declared in routes/index.tsx without ROUTE_PATHS entry
  patterns.add('/toast-demo');
  return [...patterns];
}

export const KNOWN_ROUTE_PATTERNS: ReadonlyArray<string> =
  collectKnownRoutePatterns();

/** Collapse duplicate slashes; trim trailing slash except for root. */
export function normalizePathname(pathname: string): string {
  const collapsed = pathname.replaceAll(/\/+/g, '/');
  if (collapsed === '/' || collapsed === '') {
    return '/';
  }
  return collapsed.endsWith('/') ? collapsed.slice(0, -1) : collapsed;
}

/**
 * Candidates to match against registered routes. Includes the full path and each
 * segment-rooted suffix so deployments under a URL prefix (e.g. /ENDUSER/preview-pages)
 * still resolve the same as site-root /preview-pages.
 */
function pathSuffixCandidates(normalizedPathname: string): Array<string> {
  if (normalizedPathname === '/') {
    return ['/'];
  }
  const segments = normalizedPathname.split('/').filter(Boolean);
  const out: Array<string> = [];
  for (let index = 0; index < segments.length; index++) {
    out.push(`/${segments.slice(index).join('/')}`);
  }
  return out;
}

function pathMatchesPattern(pattern: string, candidate: string): boolean {
  const hasSplat = pattern.includes('*');
  return Boolean(
    matchPath(
      {
        path: pattern,
        end: !hasSplat,
        caseSensitive: false,
      },
      candidate,
    ),
  );
}

/**
 * If the URL is a known app route, returns the pathname the SPA registers (e.g. `/preview-pages`).
 * When the browser path includes a deployment prefix, this is the **longest** suffix that matches a
 * route pattern so we navigate to a path React Router actually renders.
 */
export function getCanonicalSpaPathname(pathname: string): string | null {
  const normalized = normalizePathname(pathname);
  const candidates = pathSuffixCandidates(normalized);
  for (const candidate of candidates) {
    const matched = KNOWN_ROUTE_PATTERNS.some((pattern) =>
      pathMatchesPattern(pattern, candidate),
    );
    if (matched) {
      return candidate;
    }
  }
  return null;
}

export function pathnameMatchesAnyKnownRoute(pathname: string): boolean {
  return getCanonicalSpaPathname(pathname) !== null;
}
